var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
  get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
}) : x)(function(x) {
  if (typeof require !== "undefined")
    return require.apply(this, arguments);
  throw Error('Dynamic require of "' + x + '" is not supported');
});

// src/providers/ServiceProvider.tsx
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { WorkflowService as WorkflowServiceClass } from "@ferp/business-services";
import {
  failure,
  RepositoryError,
  ServiceFactory,
  success
} from "@ferp/core-infrastructure";
import { WorkflowRepository } from "@ferp/repositories";
import { jsx, jsxs } from "react/jsx-runtime";
var ServiceContext = createContext(null);
var ServiceProvider = ({ config, children }) => {
  const [state, setState] = useState({
    container: null,
    initializing: true,
    error: null,
    healthStatus: null
  });
  const initializeServices = useCallback(async () => {
    try {
      config.logger.info("Initializing service container");
      const factoryResult = await ServiceFactory.initialize(
        config.serviceFactory,
        config.tenantContext,
        config.logger
      );
      if (factoryResult.isFailure()) {
        return factoryResult;
      }
      const serviceFactory = factoryResult.unwrap();
      const repositoryConfig = {
        client: config.apolloClient,
        logger: config.logger,
        operationTimeout: 3e4,
        retryPolicy: {
          attempts: 3,
          delay: 1e3,
          backoffMultiplier: 2
        },
        cache: {
          enabled: config.features.caching,
          ttl: 3e5
          // 5 minutes
        }
      };
      serviceFactory.registerService(
        "WorkflowRepository",
        async () => new WorkflowRepository(repositoryConfig),
        {
          singleton: true,
          healthCheck: async () => {
            return config.apolloClient.query({
              query: __require("graphql-tag")`
                query HealthCheck {
                  __typename
                }
              `,
              fetchPolicy: "network-only"
            }).then(() => true).catch(() => false);
          }
        }
      );
      serviceFactory.registerService(
        "WorkflowService",
        async () => {
          const workflowRepo = serviceFactory.getServiceSync("WorkflowRepository");
          return new WorkflowServiceClass(config.workflowService, config.logger, workflowRepo);
        },
        {
          singleton: true,
          dependencies: ["WorkflowRepository"],
          healthCheck: async () => {
            const service = serviceFactory.getServiceSync("WorkflowService");
            const health = service.getHealthStatus();
            return health.healthy;
          }
        }
      );
      const workflowServiceResult = await serviceFactory.getService("WorkflowService");
      if (workflowServiceResult.isFailure()) {
        return workflowServiceResult;
      }
      const workflowService = workflowServiceResult.unwrap();
      const container = {
        serviceFactory,
        workflowService,
        logger: config.logger,
        async getHealthStatus() {
          const factoryHealth = serviceFactory.getFactoryHealth();
          return {
            healthy: factoryHealth.healthy,
            services: factoryHealth.services.map((service) => ({
              name: service.name,
              healthy: service.healthy,
              lastCheck: service.lastCheck,
              errorRate: service.responseTime ? 0 : 1,
              // Simplified
              averageResponseTime: service.responseTime || 0
            })),
            timestamp: /* @__PURE__ */ new Date()
          };
        },
        async getMetrics() {
          const workflowMetrics = workflowService.getOperationMetrics();
          const totalOps = workflowMetrics.reduce((sum, m) => sum + m.totalCalls, 0);
          const totalErrors = workflowMetrics.reduce((sum, m) => sum + m.failedCalls, 0);
          const totalTime = workflowMetrics.reduce((sum, m) => sum + m.totalDuration, 0);
          return {
            totalOperations: totalOps,
            successfulOperations: totalOps - totalErrors,
            failedOperations: totalErrors,
            averageResponseTime: totalOps > 0 ? totalTime / totalOps : 0,
            errorRate: totalOps > 0 ? totalErrors / totalOps : 0,
            lastHour: {
              operations: 0,
              // Would need time-based tracking
              errors: 0
            }
          };
        }
      };
      config.logger.info("Service container initialized successfully");
      return success(container);
    } catch (error) {
      config.logger.error("Service container initialization failed");
      return failure(RepositoryError.fromUnknown(error, {
        component: "ServiceProvider",
        operation: "initializeServices",
        tenant: config.tenantContext.getCurrentTenant()
      }));
    }
  }, [config]);
  useEffect(() => {
    let mounted = true;
    const initialize = async () => {
      setState((prev) => ({ ...prev, initializing: true, error: null }));
      const result = await initializeServices();
      if (!mounted)
        return;
      if (result.isSuccess()) {
        setState({
          container: result.unwrap(),
          initializing: false,
          error: null,
          healthStatus: null
        });
      } else {
        setState({
          container: null,
          initializing: false,
          error: result.unwrapError(),
          healthStatus: null
        });
      }
    };
    initialize();
    return () => {
      mounted = false;
    };
  }, [initializeServices]);
  useEffect(() => {
    if (!state.container || !config.features.healthChecking) {
      return;
    }
    const interval = setInterval(async () => {
      try {
        const healthStatus = await state.container.getHealthStatus();
        setState((prev) => ({ ...prev, healthStatus }));
        const unhealthyServices = healthStatus.services.filter((s) => !s.healthy);
        if (unhealthyServices.length > 0) {
          config.logger.warn("Unhealthy services detected");
        }
      } catch (error) {
        config.logger.error("Health check failed");
      }
    }, 6e4);
    return () => clearInterval(interval);
  }, [state.container, config.features.healthChecking, config.tenantContext, config.logger]);
  useEffect(() => {
    return () => {
      if (state.container) {
        state.container.serviceFactory.shutdown().catch((error) => {
          config.logger.error("Service factory shutdown failed");
        });
      }
    };
  }, [state.container, config.logger]);
  if (state.initializing) {
    return /* @__PURE__ */ jsxs("div", { className: "ferp-service-provider-loading", children: [
      /* @__PURE__ */ jsx("div", { className: "loading-spinner" }),
      /* @__PURE__ */ jsx("p", { children: "Initializing services..." })
    ] });
  }
  if (state.error) {
    return /* @__PURE__ */ jsxs("div", { className: "ferp-service-provider-error", children: [
      /* @__PURE__ */ jsx("h2", { children: "Service Initialization Failed" }),
      /* @__PURE__ */ jsx("p", { children: state.error.userMessage || state.error.message }),
      /* @__PURE__ */ jsxs("details", { children: [
        /* @__PURE__ */ jsx("summary", { children: "Technical Details" }),
        /* @__PURE__ */ jsx("pre", { children: JSON.stringify(state.error.context, null, 2) })
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => window.location.reload(),
          className: "retry-button",
          children: "Retry"
        }
      )
    ] });
  }
  if (state.container) {
    return /* @__PURE__ */ jsxs(ServiceContext.Provider, { value: state.container, children: [
      config.features.healthChecking && state.healthStatus && !state.healthStatus.healthy && /* @__PURE__ */ jsx("div", { className: "ferp-service-health-warning", children: "\u26A0\uFE0F Some services are unhealthy. System may be degraded." }),
      children
    ] });
  }
  return null;
};
var useServices = () => {
  const container = useContext(ServiceContext);
  if (!container) {
    throw new Error(
      "useServices must be used within a ServiceProvider. Ensure your component tree is wrapped with <ServiceProvider>."
    );
  }
  return container;
};
var useWorkflowService = () => {
  const { workflowService } = useServices();
  return workflowService;
};
var useServiceHealth = () => {
  const container = useServices();
  const [healthStatus, setHealthStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    if (refreshing)
      return;
    setRefreshing(true);
    try {
      const status = await container.getHealthStatus();
      setHealthStatus(status);
    } catch (error) {
      container.logger.error("Health status refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [container, refreshing]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return {
    healthStatus,
    isHealthy: healthStatus?.healthy ?? false,
    refresh
  };
};
var useServiceMetrics = () => {
  const container = useServices();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    if (loading)
      return;
    setLoading(true);
    try {
      const serviceMetrics = await container.getMetrics();
      setMetrics(serviceMetrics);
    } catch (error) {
      container.logger.error("Metrics refresh failed");
    } finally {
      setLoading(false);
    }
  }, [container, loading]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return {
    metrics,
    loading,
    refresh
  };
};

// src/providers/TenantProvider.tsx
import { createContext as createContext2, useContext as useContext2, useEffect as useEffect2, useState as useState2, useCallback as useCallback2 } from "react";
import {
  TenantContext,
  TenantContextFactory,
  RepositoryError as RepositoryError2,
  success as success2,
  failure as failure2
} from "@ferp/core-infrastructure";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TenantReactContext = createContext2(null);
var TenantProvider = ({ config, children }) => {
  const [authState, setAuthState] = useState2({
    authenticated: false,
    tenant: null,
    user: null,
    context: null,
    loading: true,
    error: null
  });
  const initializeTenantContext = useCallback2(async (tenantId, userId, authData) => {
    try {
      const context = await TenantContextFactory.fromAuth(tenantId, userId, authData);
      return success2(context);
    } catch (error) {
      return failure2(RepositoryError2.fromUnknown(error, {
        component: "TenantProvider",
        operation: "initializeTenantContext",
        tenant: tenantId,
        userId
      }));
    }
  }, []);
  const loadAuthState = useCallback2(async () => {
    setAuthState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const auth = await config.authProvider.getCurrentAuth();
      if (!auth) {
        if (config.systemTenant && !config.validation.requireAuthentication) {
          const systemContext = new TenantContext(config.systemTenant);
          setAuthState({
            authenticated: false,
            tenant: config.systemTenant,
            user: null,
            context: systemContext,
            loading: false,
            error: null
          });
        } else {
          setAuthState({
            authenticated: false,
            tenant: null,
            user: null,
            context: null,
            loading: false,
            error: null
          });
        }
        return;
      }
      const contextResult = await initializeTenantContext(
        auth.tenantId,
        auth.userId,
        auth.authData
      );
      if (contextResult.isFailure()) {
        setAuthState({
          authenticated: false,
          tenant: null,
          user: null,
          context: null,
          loading: false,
          error: contextResult.unwrapError()
        });
        return;
      }
      const tenantContext = contextResult.unwrap();
      setAuthState({
        authenticated: true,
        tenant: tenantContext.tenant,
        user: tenantContext.user,
        context: tenantContext,
        loading: false,
        error: null
      });
    } catch (error) {
      const repoError = RepositoryError2.fromUnknown(error, {
        component: "TenantProvider",
        operation: "loadAuthState"
      });
      setAuthState({
        authenticated: false,
        tenant: null,
        user: null,
        context: null,
        loading: false,
        error: repoError
      });
    }
  }, [config, initializeTenantContext]);
  const signOut = useCallback2(async () => {
    try {
      await config.authProvider.signOut();
      setAuthState({
        authenticated: false,
        tenant: null,
        user: null,
        context: null,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  }, [config.authProvider]);
  const refresh = useCallback2(async () => {
    await loadAuthState();
  }, [loadAuthState]);
  const switchTenant = useCallback2(async (tenantId) => {
    if (!authState.user) {
      return failure2(RepositoryError2.unauthorized("switch tenant", {
        component: "TenantProvider",
        operation: "switchTenant"
      }));
    }
    try {
      const contextResult = await initializeTenantContext(
        tenantId,
        authState.user.id,
        {
          /* updated auth data */
        }
      );
      if (contextResult.isFailure()) {
        return contextResult;
      }
      const newContext = contextResult.unwrap();
      setAuthState((prev) => ({
        ...prev,
        tenant: newContext.tenant,
        context: newContext,
        error: null
      }));
      return success2(void 0);
    } catch (error) {
      return failure2(RepositoryError2.fromUnknown(error, {
        component: "TenantProvider",
        operation: "switchTenant",
        metadata: { targetTenant: tenantId }
      }));
    }
  }, [authState.user, initializeTenantContext]);
  const validateAccess = useCallback2(async (resource, operation) => {
    if (!authState.context) {
      return false;
    }
    return await authState.context.validateAccess(resource, operation);
  }, [authState.context]);
  const createOperationContext = useCallback2((reason, metadata) => {
    if (!authState.context) {
      return null;
    }
    return {
      tenantContext: authState.context,
      reason,
      requestId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      correlationId: authState.context.getCorrelationId(),
      metadata
    };
  }, [authState.context]);
  const isAuthenticated = useCallback2(() => {
    return authState.authenticated && authState.context !== null;
  }, [authState]);
  const hasPermission = useCallback2((permission) => {
    return authState.context?.hasPermission(permission) ?? false;
  }, [authState.context]);
  const hasRole = useCallback2((role) => {
    return authState.context?.hasRole(role) ?? false;
  }, [authState.context]);
  const isFeatureEnabled = useCallback2((feature) => {
    return authState.context?.isFeatureEnabled(feature) ?? false;
  }, [authState.context]);
  useEffect2(() => {
    loadAuthState();
    const unsubscribe = config.authProvider.onAuthChange((auth) => {
      loadAuthState();
    });
    return unsubscribe;
  }, [loadAuthState, config.authProvider]);
  useEffect2(() => {
    if (!authState.authenticated || !config.validation.sessionTimeout) {
      return;
    }
    const timeout = setTimeout(() => {
      signOut();
    }, config.validation.sessionTimeout);
    return () => clearTimeout(timeout);
  }, [authState.authenticated, config.validation.sessionTimeout, signOut]);
  useEffect2(() => {
    if (config.validation.requireTenantIsolation && authState.authenticated && !authState.tenant) {
      console.error("CRITICAL: Tenant isolation violation - authenticated user without tenant context");
      signOut();
    }
  }, [authState, config.validation.requireTenantIsolation, signOut]);
  const contextValue = {
    authState,
    tenantContext: authState.context,
    signOut,
    refresh,
    switchTenant,
    validateAccess,
    createOperationContext,
    isAuthenticated,
    hasPermission,
    hasRole,
    isFeatureEnabled
  };
  if (authState.loading) {
    return /* @__PURE__ */ jsxs2("div", { className: "ferp-tenant-provider-loading", children: [
      /* @__PURE__ */ jsx2("div", { className: "loading-spinner" }),
      /* @__PURE__ */ jsx2("p", { children: "Loading authentication..." })
    ] });
  }
  if (authState.error) {
    return /* @__PURE__ */ jsxs2("div", { className: "ferp-tenant-provider-error", children: [
      /* @__PURE__ */ jsx2("h2", { children: "Authentication Error" }),
      /* @__PURE__ */ jsx2("p", { children: authState.error.userMessage || "Authentication failed" }),
      /* @__PURE__ */ jsx2("button", { onClick: refresh, className: "retry-button", children: "Retry" })
    ] });
  }
  if (config.validation.requireAuthentication && !authState.authenticated) {
    return /* @__PURE__ */ jsxs2("div", { className: "ferp-tenant-provider-unauthenticated", children: [
      /* @__PURE__ */ jsx2("h2", { children: "Authentication Required" }),
      /* @__PURE__ */ jsx2("p", { children: "Please sign in to access this application." })
    ] });
  }
  return /* @__PURE__ */ jsx2(TenantReactContext.Provider, { value: contextValue, children });
};
var useTenantContext = () => {
  const value = useContext2(TenantReactContext);
  if (!value) {
    throw new Error(
      "useTenantContext must be used within a TenantProvider. Ensure your component tree is wrapped with <TenantProvider>."
    );
  }
  if (!value.tenantContext) {
    throw new Error(
      "No tenant context available. User may not be authenticated or tenant isolation may be compromised."
    );
  }
  return value.tenantContext;
};
var useAuth = () => {
  const value = useContext2(TenantReactContext);
  if (!value) {
    throw new Error(
      "useAuth must be used within a TenantProvider. Ensure your component tree is wrapped with <TenantProvider>."
    );
  }
  return {
    authenticated: value.authState.authenticated,
    user: value.authState.user,
    tenant: value.authState.tenant,
    loading: value.authState.loading,
    error: value.authState.error,
    signOut: value.signOut,
    refresh: value.refresh
  };
};
var useOperationContext = () => {
  const value = useContext2(TenantReactContext);
  if (!value) {
    throw new Error(
      "useOperationContext must be used within a TenantProvider. Ensure your component tree is wrapped with <TenantProvider>."
    );
  }
  return {
    createContext: (reason, metadata) => {
      const context = value.createOperationContext(reason, metadata);
      if (!context) {
        throw new Error("Cannot create operation context - user not authenticated");
      }
      return context;
    }
  };
};
var usePermissions = () => {
  const value = useContext2(TenantReactContext);
  if (!value) {
    throw new Error(
      "usePermissions must be used within a TenantProvider. Ensure your component tree is wrapped with <TenantProvider>."
    );
  }
  return {
    hasPermission: value.hasPermission,
    hasRole: value.hasRole,
    hasAnyRole: (roles) => roles.some((role) => value.hasRole(role)),
    hasAllRoles: (roles) => roles.every((role) => value.hasRole(role)),
    validateAccess: value.validateAccess
  };
};
var useFeatures = () => {
  const value = useContext2(TenantReactContext);
  if (!value) {
    throw new Error(
      "useFeatures must be used within a TenantProvider. Ensure your component tree is wrapped with <TenantProvider>."
    );
  }
  return {
    isFeatureEnabled: value.isFeatureEnabled,
    hasFeature: value.isFeatureEnabled
  };
};

// src/hooks/useWorkflow.ts
import { useState as useState3, useEffect as useEffect3, useCallback as useCallback3, useRef } from "react";
import {
  RepositoryError as RepositoryError3
} from "@ferp/core-infrastructure";
var useCreateWorkflow = () => {
  const workflowService = useWorkflowService();
  const { createContext: createContext3 } = useOperationContext();
  const [loading, setLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const createWorkflow = useCallback3(async (request) => {
    setLoading(true);
    setError(null);
    try {
      const context = createContext3("Create workflow via UI", {
        source: "useCreateWorkflow",
        workflowName: request.name
      });
      const result = await workflowService.createWorkflow(request, context);
      if (result.isFailure()) {
        setError(result.unwrapError());
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [workflowService, createContext3]);
  return { createWorkflow, loading, error };
};
var useUpdateWorkflow = () => {
  const workflowService = useWorkflowService();
  const { createContext: createContext3 } = useOperationContext();
  const [loading, setLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const updateWorkflow = useCallback3(async (id, request) => {
    setLoading(true);
    setError(null);
    try {
      const context = createContext3("Update workflow via UI", {
        source: "useUpdateWorkflow",
        workflowId: id
      });
      const result = await workflowService.updateWorkflow(id, request, context);
      if (result.isFailure()) {
        setError(result.unwrapError());
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [workflowService, createContext3]);
  return { updateWorkflow, loading, error };
};
var useWorkflow = (workflowId) => {
  const workflowService = useWorkflowService();
  const tenantContext = useTenantContext();
  const [state, setState] = useState3({
    data: null,
    loading: false,
    error: null
  });
  const fetchWorkflow = useCallback3(async (id) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await workflowService.getWorkflow(id, tenantContext);
      if (result.isSuccess()) {
        setState({
          data: result.unwrap(),
          loading: false,
          error: null
        });
      } else {
        setState({
          data: null,
          loading: false,
          error: result.unwrapError()
        });
      }
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: RepositoryError3.fromUnknown(error, {
          component: "useWorkflow",
          operation: "fetchWorkflow",
          resourceId: id
        })
      });
    }
  }, [workflowService, tenantContext]);
  useEffect3(() => {
    if (workflowId) {
      fetchWorkflow(workflowId);
    } else {
      setState({
        data: null,
        loading: false,
        error: null
      });
    }
  }, [workflowId, fetchWorkflow]);
  return state;
};
var useWorkflows = (filters = [], options = {}) => {
  const workflowService = useWorkflowService();
  const tenantContext = useTenantContext();
  const [state, setState] = useState3({
    data: null,
    loading: false,
    error: null,
    hasMore: false
  });
  const currentOffset = useRef(0);
  const filtersRef = useRef(filters);
  const optionsRef = useRef(options);
  useEffect3(() => {
    filtersRef.current = filters;
    optionsRef.current = options;
    currentOffset.current = 0;
  }, [filters, options]);
  const fetchWorkflows = useCallback3(async (append = false) => {
    const offset = append ? currentOffset.current : 0;
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      data: append && prev.data ? prev.data : null
    }));
    try {
      const result = await workflowService.listWorkflows(
        filtersRef.current,
        tenantContext,
        {
          limit: optionsRef.current.limit || 50,
          offset
        }
      );
      if (result.isSuccess()) {
        const workflows = result.unwrap();
        const newData = append && state.data ? [...state.data, ...workflows] : workflows;
        const hasMore = workflows.length === (optionsRef.current.limit || 50);
        currentOffset.current = offset + workflows.length;
        setState({
          data: newData,
          loading: false,
          error: null,
          hasMore
        });
      } else {
        setState((prev) => ({
          data: append ? prev.data : null,
          loading: false,
          error: result.unwrapError(),
          hasMore: false
        }));
      }
    } catch (error) {
      setState((prev) => ({
        data: append ? prev.data : null,
        loading: false,
        error: RepositoryError3.fromUnknown(error, {
          component: "useWorkflows",
          operation: "fetchWorkflows"
        }),
        hasMore: false
      }));
    }
  }, [workflowService, tenantContext, state.data]);
  const refresh = useCallback3(async () => {
    currentOffset.current = 0;
    await fetchWorkflows(false);
  }, [fetchWorkflows]);
  const loadMore = useCallback3(async () => {
    if (state.hasMore && !state.loading) {
      await fetchWorkflows(true);
    }
  }, [fetchWorkflows, state.hasMore, state.loading]);
  useEffect3(() => {
    fetchWorkflows(false);
  }, [tenantContext]);
  useEffect3(() => {
    if (!options.autoRefresh)
      return;
    const interval = setInterval(() => {
      refresh();
    }, 3e4);
    return () => clearInterval(interval);
  }, [options.autoRefresh, refresh]);
  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    hasMore: state.hasMore,
    refresh,
    loadMore
  };
};
var useActiveWorkflows = () => {
  const workflowService = useWorkflowService();
  const tenantContext = useTenantContext();
  const [state, setState] = useState3({
    data: null,
    loading: false,
    error: null
  });
  const fetchActiveWorkflows = useCallback3(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await workflowService.getActiveWorkflows(tenantContext);
      if (result.isSuccess()) {
        setState({
          data: result.unwrap(),
          loading: false,
          error: null
        });
      } else {
        setState({
          data: null,
          loading: false,
          error: result.unwrapError()
        });
      }
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: RepositoryError3.fromUnknown(error, {
          component: "useActiveWorkflows",
          operation: "fetchActiveWorkflows"
        })
      });
    }
  }, [workflowService, tenantContext]);
  const refresh = useCallback3(async () => {
    await fetchActiveWorkflows();
  }, [fetchActiveWorkflows]);
  useEffect3(() => {
    fetchActiveWorkflows();
  }, [fetchActiveWorkflows]);
  return { ...state, refresh };
};
var useWorkflowActivation = () => {
  const workflowService = useWorkflowService();
  const { createContext: createContext3 } = useOperationContext();
  const [loading, setLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const activateWorkflow = useCallback3(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const context = createContext3("Activate workflow via UI", {
        source: "useWorkflowActivation",
        workflowId: id,
        action: "activate"
      });
      const result = await workflowService.activateWorkflow(id, context);
      if (result.isFailure()) {
        setError(result.unwrapError());
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [workflowService, createContext3]);
  const deactivateWorkflow = useCallback3(async (id, reason) => {
    setLoading(true);
    setError(null);
    try {
      const context = createContext3(reason, {
        source: "useWorkflowActivation",
        workflowId: id,
        action: "deactivate"
      });
      const result = await workflowService.deactivateWorkflow(id, reason, context);
      if (result.isFailure()) {
        setError(result.unwrapError());
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [workflowService, createContext3]);
  return { activateWorkflow, deactivateWorkflow, loading, error };
};
var useWorkflowValidation = () => {
  const workflowService = useWorkflowService();
  const { createContext: createContext3 } = useOperationContext();
  const [loading, setLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const validateWorkflow = useCallback3(async (definition) => {
    setLoading(true);
    setError(null);
    try {
      const context = createContext3("Validate workflow via UI", {
        source: "useWorkflowValidation",
        nodeCount: definition.nodes.length,
        edgeCount: definition.edges.length
      });
      const result = await workflowService.validateWorkflowDefinition(definition, context);
      if (result.isFailure()) {
        setError(result.unwrapError());
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [workflowService, createContext3]);
  return { validateWorkflow, loading, error };
};
var useDeleteWorkflow = () => {
  const workflowService = useWorkflowService();
  const { createContext: createContext3 } = useOperationContext();
  const [loading, setLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const deleteWorkflow = useCallback3(async (id, force = false) => {
    setLoading(true);
    setError(null);
    try {
      const context = createContext3("Delete workflow via UI", {
        source: "useDeleteWorkflow",
        workflowId: id,
        force
      });
      const result = await workflowService.deleteWorkflow(id, context, force);
      if (result.isFailure()) {
        setError(result.unwrapError());
      }
      return result;
    } finally {
      setLoading(false);
    }
  }, [workflowService, createContext3]);
  return { deleteWorkflow, loading, error };
};
export {
  ServiceProvider,
  TenantProvider,
  useActiveWorkflows,
  useAuth,
  useCreateWorkflow,
  useDeleteWorkflow,
  useFeatures,
  useOperationContext,
  usePermissions,
  useServiceHealth,
  useServiceMetrics,
  useServices,
  useTenantContext,
  useUpdateWorkflow,
  useWorkflow,
  useWorkflowActivation,
  useWorkflowService,
  useWorkflowValidation,
  useWorkflows
};
//# sourceMappingURL=index.mjs.map